import { NavLink } from 'react-router-dom'
import { motion } from 'framer-motion'
import { HiChevronLeft } from 'react-icons/hi2'
import { NAV_GROUPS, NAV_ITEMS } from '@/constants/navigation'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { useQuery } from '@tanstack/react-query'
import { grievancesApi, meetingsApi, intakesApi } from '@/services/api'
import { actionsStore } from '@/modules/action-management/store'
import { isOpen } from '@/modules/action-management/utils'
import { cn } from '@/utils/cn'
import { Tooltip } from '@/components/ui/Tooltip'

export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggle = useUIStore((s) => s.toggleSidebar)
  const setMobileNav = useUIStore((s) => s.setMobileNav)
  const can = useAuthStore((s) => s.can)

  const { data: badges } = useQuery({
    queryKey: ['nav-badges'],
    queryFn: async () => {
      const [g, t, m, i] = await Promise.all([grievancesApi.all(), actionsStore.all(), meetingsApi.all(), intakesApi.all()])
      const today = new Date().toDateString()
      return {
        grievances: g.filter((x) => !['resolved', 'closed', 'rejected'].includes(x.status)).length,
        tasks: t.filter(isOpen).length, // real open action-tasks
        meetings: m.filter((x) => new Date(x.start).toDateString() === today).length,
        frontDesk: i.filter((x) => x.status === 'new').length, // intakes awaiting triage
      }
    },
    staleTime: 60_000,
  })

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-border bg-card transition-[width] duration-300',
        collapsed ? 'w-[76px]' : 'w-64',
      )}
    >
      {/* Brand */}
      <div className={cn('flex h-16 items-center gap-2.5 border-b border-border px-4', collapsed && 'justify-center px-0')}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-white shadow-sm">
          <ShieldMark />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate text-sm font-bold leading-tight text-foreground">MCCMS</p>
            <p className="truncate text-[11px] text-muted-foreground">Minister's Office</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="no-scrollbar flex-1 space-y-4 overflow-y-auto px-3 py-4">
        {NAV_GROUPS.map((group) => {
          const items = NAV_ITEMS.filter((n) => n.group === group.key && can(n.permission))
          if (!items.length) return null
          return (
            <div key={group.key}>
              {!collapsed && (
                <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const badge = item.badgeKey ? (badges as any)?.[item.badgeKey] : undefined
                  const link = (
                    <NavLink
                      to={item.to}
                      onClick={() => setMobileNav(false)}
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          collapsed && 'justify-center px-0',
                          isActive
                            ? 'bg-primary-100 font-semibold text-primary-700'
                            : 'text-slate-600 hover:bg-primary-50 hover:text-primary-700 dark:hover:bg-primary-500/15',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon className="h-[18px] w-[18px] shrink-0" />
                          {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                          {!collapsed && badge > 0 && (
                            <span
                              className={cn(
                                'rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                                isActive
                                  ? 'bg-primary-200 text-primary-800 dark:bg-primary-500/25 dark:text-primary-100'
                                  : 'bg-danger-100 text-danger-700 dark:bg-danger-500/20 dark:text-danger-200',
                              )}
                            >
                              {badge}
                            </span>
                          )}
                          {collapsed && badge > 0 && (
                            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-danger" />
                          )}
                        </>
                      )}
                    </NavLink>
                  )
                  return (
                    <li key={item.to}>
                      {collapsed ? (
                        <Tooltip content={item.label} side="right">
                          {link}
                        </Tooltip>
                      ) : (
                        link
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-border p-3">
        <button
          onClick={toggle}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <motion.span animate={{ rotate: collapsed ? 180 : 0 }}>
            <HiChevronLeft className="h-4 w-4" />
          </motion.span>
          {!collapsed && 'Collapse'}
        </button>
      </div>
    </aside>
  )
}

function ShieldMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
      <path d="M12 3l7 3v5c0 4.2-2.9 7.9-7 9-4.1-1.1-7-4.8-7-9V6l7-3z" fill="currentColor" opacity="0.25" />
      <path d="M12 4.2l5.8 2.5V11c0 3.5-2.4 6.6-5.8 7.6C8.6 17.6 6.2 14.5 6.2 11V6.7L12 4.2z" stroke="currentColor" strokeWidth="1.3" />
      <path d="M9.2 11.6l2 2 3.6-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
